import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback, useRef } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import {
  Printer, ArrowLeft, ChevronRight, ChevronDown, FolderOpen, Download, Eye,
  CheckCircle2, RotateCcw, Upload, Image as ImageIcon, Ruler,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_META = {
  needs_review: { label: "Review the design", cls: "bg-amber-100 text-amber-800" },
  needs_production_file: { label: "Upload production file", cls: "bg-violet-100 text-violet-800" },
  awaiting_final: { label: "Waiting for re-upload", cls: "bg-zinc-100 text-zinc-600" },
  awaiting_client: { label: "Awaiting client approval", cls: "bg-blue-100 text-blue-800" },
  production_file_rejected: { label: "Client wants changes", cls: "bg-rose-100 text-rose-800" },
  done: { label: "Done", cls: "bg-emerald-100 text-emerald-700" },
  in_progress: { label: "In progress", cls: "bg-zinc-100 text-zinc-600" },
};

async function blobDownload(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const objectUrl = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = objectUrl; a.download = filename || "file";
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export default function PrePressWorkPortal() {
  const [projects, setProjects] = useState(null);
  const [code, setCode] = useState(null);

  const load = useCallback(() => {
    api.get("/prepress-my-work")
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setProjects([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = code ? (projects || []).find((p) => p.project_code === code) : null;

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto" data-testid="prepress-work-page">
      {projects === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : active ? (
        <ProjectView project={active} onBack={() => setCode(null)} onChanged={load} />
      ) : (
        <ProjectList projects={projects} onOpen={setCode} />
      )}
    </div>
  );
}

function ProjectList({ projects, onOpen }) {
  return (
    <div data-testid="prepress-work-list">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-violet-600 text-white flex items-center justify-center">
          <Printer size={20} strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">My Pre-Press Work</h1>
          <p className="text-sm text-zinc-500">Review approved designs and upload production files — no Project BOQ needed.</p>
        </div>
      </div>
      {projects.length === 0 ? (
        <div className="mt-10 py-20 text-center text-zinc-400 border border-dashed border-zinc-200 rounded-2xl">
          Nothing assigned to you yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {projects.map((p) => (
            <button key={p.project_code} onClick={() => onOpen(p.project_code)}
              data-testid={`prepress-work-project-${p.project_code}`}
              className="text-left rounded-2xl border border-zinc-200 shadow-sm bg-white p-5 hover:border-violet-300 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={p.project_code} /></span>
                <ChevronRight size={16} className="text-zinc-300" />
              </div>
              <div className="mt-1 font-semibold text-zinc-900 line-clamp-2">{p.project_name}</div>
              <div className="mt-4 text-sm">
                {p.todo_count > 0 ? (
                  <span className="text-violet-700 font-medium">{p.todo_count} need your action</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                    <CheckCircle2 size={14} /> all done
                  </span>
                )}
                <span className="text-zinc-400"> · {p.total} wall(s)</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const ACTIONABLE = new Set(["needs_review", "needs_production_file", "awaiting_final", "production_file_rejected"]);

function fileUrl(u) {
  if (!u) return "";
  return u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
}

function ProjectView({ project, onBack, onChanged }) {
  const groups = {};
  const order = [];
  (project.lines || []).forEach((w) => {
    if (!groups[w.group]) { groups[w.group] = []; order.push(w.group); }
    groups[w.group].push(w);
  });
  const totalAction = (project.lines || []).filter((w) => ACTIONABLE.has(w.stage)).length;

  return (
    <div data-testid="prepress-work-view">
      <button onClick={onBack} data-testid="prepress-work-back"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4">
        <ArrowLeft size={16} /> All projects
      </button>
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={project.project_code} /></span>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{project.project_name}</h1>
        </div>
        {totalAction > 0 ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 px-3 py-1.5 text-sm font-medium"
            data-testid="prepress-work-action-count">
            <span className="w-2 h-2 rounded-full bg-rose-500" /> {totalAction} wall(s) need you
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
            <CheckCircle2 size={15} /> all caught up
          </span>
        )}
      </div>
      <div className="space-y-5">
        {order.map((g) => (
          <GroupSection key={g} name={g} walls={groups[g]} code={project.project_code} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function GroupSection({ name, walls, code, onChanged }) {
  const actionCount = walls.filter((w) => ACTIONABLE.has(w.stage)).length;
  const [open, setOpen] = useState(actionCount > 0);
  return (
    <div className="rounded-2xl border border-zinc-200 shadow-sm bg-white overflow-hidden"
      data-testid={`prepress-work-group-${name}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`prepress-work-group-toggle-${name}`}
        className="w-full flex items-center gap-2 px-5 py-3.5 bg-zinc-50 border-b border-zinc-200 text-left hover:bg-zinc-100/70 transition-colors">
        <ChevronDown size={16} className={`text-zinc-400 transition-transform ${open ? "" : "-rotate-90"}`} />
        <FolderOpen size={18} className="text-violet-600" strokeWidth={1.6} />
        <span className="font-semibold text-zinc-900">{name}</span>
        <span className="text-xs text-zinc-400">· {walls.length} wall(s)</span>
        {actionCount > 0 && (
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 px-2.5 py-0.5 text-xs font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> {actionCount} need action
          </span>
        )}
      </button>
      {open && (
        <div className="divide-y divide-zinc-100">
          {walls.map((w) => (
            <WallRow key={w.boq_id} w={w} code={code} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

function WallRow({ w, code, onChanged }) {
  const meta = STAGE_META[w.stage] || STAGE_META.in_progress;
  const actionable = ACTIONABLE.has(w.stage);
  const [open, setOpen] = useState(actionable);
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const thumb = w.has_final ? fileUrl(w.final_design_url) : "";

  async function review(action) {
    let comment = "";
    if (action === "rework") {
      comment = window.prompt("What should the designer fix? (e.g. low resolution, wrong colour space, missing bleed)") || "";
      if (!comment.trim()) { toast.error("A comment is required to request changes"); return; }
    }
    setBusy(true);
    try {
      await api.post(`/projects/${code}/boq/${w.boq_id}/final-design/review?action=${action}`, { comment: comment.trim() });
      toast.success(action === "accept" ? "Design accepted — upload the production file" : "Sent back to the designer");
      onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  }

  async function uploadProduction(file) {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("project_code", code);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/projects/${code}/boq/${w.boq_id}/production-file`, {
        file_url: up.data.url || up.data.file_url, file_name: file.name, note: "",
      });
      toast.success("Production file uploaded — sent to client for approval");
      onChanged();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className={`border-l-4 ${actionable ? "border-l-rose-500 bg-rose-50/30" : "border-l-transparent"}`}
      data-testid={`prepress-work-wall-${w.boq_id}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`prepress-work-wall-toggle-${w.boq_id}`}
        className="w-full px-5 py-3.5 flex items-center gap-3 text-left">
        <ChevronDown size={16} className={`text-zinc-400 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
        {thumb ? (
          <img src={thumb} alt="" className="w-10 h-10 rounded-lg object-cover border border-zinc-200 shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center justify-center shrink-0">
            <ImageIcon size={15} className="text-zinc-300" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-medium text-zinc-900 truncate">
            {w.wall_label}
            {w.linked_count > 1 && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold whitespace-nowrap"
                    data-testid={`prepress-work-linked-badge-${w.boq_id}`}>1 file × {w.linked_count}</span>
            )}
          </div>
          <div className="text-xs text-zinc-400 truncate">
            {w.title}{w.designer_name ? ` · designer ${w.designer_name}` : ""}
          </div>
        </div>
        <span className={`shrink-0 text-xs px-2.5 py-1 rounded-full font-medium ${meta.cls}`}
          data-testid={`prepress-work-stage-${w.boq_id}`}>{meta.label}</span>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 space-y-4">
          {(w.measure || w.site_photos.length > 0) && (
            <div className="flex items-center gap-4 text-xs text-zinc-500">
              {w.measure && <span className="inline-flex items-center gap-1"><Ruler size={12} /> {w.measure}</span>}
              {w.site_photos.length > 0 && <span className="inline-flex items-center gap-1"><ImageIcon size={12} /> {w.site_photos.length} site photo(s)</span>}
            </div>
          )}

          {/* Designer's final / approved design */}
          <div className="rounded-xl border border-zinc-200 p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="text-sm font-semibold text-zinc-900">Designer · Approved design</span>
              {w.final_design_status === "accepted"
                ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Accepted by you</span>
                : w.stage === "awaiting_final"
                  ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">Sent back · awaiting re-upload</span>
                  : <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Awaiting your review</span>}
            </div>
            {w.has_final ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-zinc-500 truncate max-w-[220px]">{w.final_design_name || "design file"}</span>
                <a href={thumb} target="_blank" rel="noreferrer" data-testid={`prepress-work-view-${w.boq_id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50">
                  <Eye size={15} /> View
                </a>
                <button onClick={() => blobDownload(`/projects/${code}/boq/${w.boq_id}/final-design/download`, w.final_design_name)}
                  data-testid={`prepress-work-download-${w.boq_id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50">
                  <Download size={15} /> Download
                </button>
              </div>
            ) : (
              <p className="text-sm text-zinc-400">
                {w.final_design_review_note ? `You asked: "${w.final_design_review_note}" — ` : ""}waiting for the designer to re-upload.
              </p>
            )}
            {w.stage === "needs_review" && (
              <div className="mt-4 pt-3 border-t border-zinc-100">
                <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 mb-2">Pre-press decision</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button disabled={busy} onClick={() => review("accept")} data-testid={`prepress-work-accept-${w.boq_id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 text-white px-3.5 py-2 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                    <CheckCircle2 size={15} /> Approve design
                  </button>
                  <button disabled={busy} onClick={() => review("rework")} data-testid={`prepress-work-rework-${w.boq_id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 text-amber-700 px-3.5 py-2 text-sm font-medium hover:bg-amber-50 disabled:opacity-50">
                    <RotateCcw size={15} /> Request modification
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Production file */}
          {w.final_design_status === "accepted" && (
            <div className="rounded-xl border border-zinc-200 p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-sm font-semibold text-zinc-900">Pre-Press · Production file</span>
                {w.stage === "awaiting_client" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Awaiting client approval</span>}
                {w.stage === "done" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Client approved</span>}
                {w.stage === "production_file_rejected" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Client wants changes</span>}
              </div>
              {w.production_file_client_note && (
                <div className="mb-3 text-xs text-rose-600">Client note: {w.production_file_client_note}</div>
              )}
              {(w.stage === "needs_production_file" || w.stage === "production_file_rejected") ? (
                <>
                  <input ref={fileInput} type="file" className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.svg,.ai,.cdr,.eps,.psd,.tif,.tiff,.indd,.dxf,.dwg,.zip"
                    onChange={(e) => uploadProduction(e.target.files?.[0])} data-testid={`prepress-work-file-${w.boq_id}`} />
                  <button disabled={busy} onClick={() => fileInput.current?.click()} data-testid={`prepress-work-upload-${w.boq_id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 text-white px-3.5 py-2 text-sm font-medium hover:bg-violet-700 disabled:opacity-50">
                    <Upload size={15} /> {busy ? "Uploading…" : (w.stage === "production_file_rejected" ? "Re-upload production file" : "Upload production file")}
                  </button>
                </>
              ) : w.production_file_url ? (
                <a href={fileUrl(w.production_file_url)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 px-3 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50">
                  <Eye size={15} /> View production file
                </a>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
